import { assertIsTruthy } from '@llx/common';
import gql from 'graphql-tag';
import { createContext, Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  AnswerFieldsFragment,
  NpsScoreFieldsFragment,
  useMeetingStatsViewContextQuery,
} from '@/@generated/codegen/API';
import usePatientCaseContent from '@/features/caseCluster/hooks/usePatientCaseContent';
import { MEETING_DEFAULT_LOCATION } from '@/features/meeting/helpers/constants';
import { useAnswersByMeetingID } from '@/hooks/useAnswersByMeetingID';
import useNavigateHomeOnMeetingDeletion from '@/hooks/useNavigateHomeOnMeetingDeletion';
import useUserMeetingJoins from '@/hooks/useUserMeetingJoins';
import EmptyPageWithTitle from '@/pages/page404/EmptyPageWithTitle.tsx';
import { PageLoading } from '@/ui/PageLoading';
import getUniquePresentedCases from '@/utils/helpers/getAmountOfCasesPresented';

import useMeetingAssetStats from '../hooks/useMeetingAssetStats';
import useMeetingEngagementScore, {
  CaseLLXScoreDetailsRow,
} from '../hooks/useMeetingEngagementScore';
import useMeetingNPSScores from '../hooks/useMeetingNPSScores';
import useMeetingSlideStats from '../hooks/useMeetingSlideStats';
import useMeetingStatsData from '../hooks/useMeetingStatsData';
import {
  calculateConsensusRate,
  ConsensusResult,
  ConsensusReturnType,
  getDefaultConsensusReturn,
} from '../utils/calculateConsensusRate';
import { ParsedAssetViewsPerCase } from '../utils/parseAssetViewsPerCase';
import { ParsedSlideStatsPerCase } from '../utils/parseSlideStatsPerCase';

type Props = {
  children: React.ReactNode;
};

type MeetingStatsViewContextType = {
  currentPatientCaseId: string;
  setCurrentPatientCaseId: Dispatch<SetStateAction<string>>;
  caseIds: string[];
  caseTitles: Record<string, string>;
  parsedAssetViews: ParsedAssetViewsPerCase | undefined;
  parsedSlideStats: ParsedSlideStatsPerCase | undefined;
  consensusRate: number;
  consensusRatePerCase: Record<string, number>;
  consensusResultsPerCase: Record<string, ConsensusResult>;
  totalDuration: number;
  longestCaseDuration: number;
  durationPerCaseInSeconds: Record<string, number> | undefined;
  longestSlidePerCaseInSeconds: Record<string, number> | undefined;
  engagementScore: number;
  npsScore: number;
  amtNpsScores: number;
  scoresPerUser: Map<string, NpsScoreFieldsFragment[]>;
  caseIdToLLXRating: Map<string, number>;
  caseIdToLLXRatingDetails: Map<string, CaseLLXScoreDetailsRow[]>;
  meetingDate: number;
  meetingLocation: string;
  meetingHostName: string;
  uniqueCasesPresented: string[];
  answerList: AnswerFieldsFragment[];
  maxAmtParticipants: number;
};

gql`
  query MeetingStatsViewContext($where: MeetingWhereUniqueInput!) {
    getMeeting(where: $where) {
      ...MeetingFields
      ...MeetingRelationCaseCluster
    }
  }
`;

export const MeetingStatsViewContext = createContext({} as MeetingStatsViewContextType);

export default function MeetingStatsViewContextProvider({ children }: Props) {
  const { meetingID } = useParams();
  // NOTE: it has to be defined since it is part of the url
  assertIsTruthy(meetingID);
  const [currentPatientCaseId, setCurrentPatientCaseId] = useState<string>('');
  const { npsAverage, scoresPerUser, npsScores } = useMeetingNPSScores(meetingID, true);
  const userMeetingJoinsQuery = useUserMeetingJoins(meetingID);
  const userMeetingJoins = useMemo(() => {
    return userMeetingJoinsQuery.data?.listUserMeetingJoins.edges.map((el) => el.node) || [];
  }, [userMeetingJoinsQuery.data]);
  useNavigateHomeOnMeetingDeletion({
    meetingID,
  });

  const meetingDetailsQuery = useMeetingStatsViewContextQuery({
    variables: {
      where: {
        id: meetingID,
      },
    },
    skip: !meetingID,
  });
  const meetingDetails = useMemo(() => {
    return meetingDetailsQuery.data?.getMeeting;
  }, [meetingDetailsQuery.data]);

  const meetingStatsData = useMeetingStatsData(meetingID, true);

  const caseIds = useMemo(() => {
    if (!meetingDetails?.caseCluster?.cases) return [];
    return meetingDetails?.caseCluster?.cases.edges.map((el) => el.node.id);
  }, [meetingDetails]);

  const { caseMap, isCaseMapLoading: isCaseLoading } = usePatientCaseContent(caseIds);

  const caseTitles = useMemo(() => {
    if (isCaseLoading || Object.keys(caseMap).length === 0) {
      return {};
    }
    return Object.keys(caseMap).reduce(
      (acc, curr) => {
        acc[curr] = caseMap[curr].displayName;
        return acc;
      },
      {} as Record<string, string>
    );
  }, [caseMap, isCaseLoading]);

  const uniqueCasesPresented = useMemo(() => {
    return getUniquePresentedCases(meetingStatsData?.casePresentationHistory);
  }, [meetingStatsData?.casePresentationHistory]);
  // NOTE: this was previously used to first perform a sanity check, but we removed it
  // we are keeping this activated flag in case we need something similar in the future
  const activated = true;

  const { parsedSlideStats } = useMeetingSlideStats(
    activated,
    isCaseLoading || caseIds.length === 0,
    meetingID,
    caseMap,
    caseIds
  );

  const { parsedAssetViews } = useMeetingAssetStats(
    activated,
    isCaseLoading || caseIds.length === 0,
    meetingID,
    caseMap,
    caseIds
  );

  //Note: this is in seconds
  const durationPerCaseInSeconds = useMemo(() => {
    if (caseIds.length === 0 || !parsedSlideStats) return undefined;

    return caseIds.reduce(
      (acc, curr) => {
        const duration = parsedSlideStats[curr].reduce((acc1, curr1) => acc1 + curr1.time, 0);
        acc[curr] = duration;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [parsedSlideStats, caseIds]);
  const longestSlidePerCaseInSeconds = useMemo(() => {
    if (caseIds.length === 0 || !parsedSlideStats) return undefined;
    return caseIds.reduce(
      (acc, curr) => {
        const maxSlide = parsedSlideStats[curr].reduce((max, current) => {
          return current.time > max ? current.time : max;
        }, 0);
        acc[curr] = maxSlide;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [caseIds, parsedSlideStats]);

  const { answerList, isLoading: isAnswerListLoading } = useAnswersByMeetingID(meetingID);
  const { consensusRate, consensusRatePerCase, consensusResultsPerCase } =
    useMemo<ConsensusReturnType>(() => {
      if (caseIds.length === 0 || isAnswerListLoading) return getDefaultConsensusReturn(caseIds);
      return calculateConsensusRate(caseIds, answerList);
    }, [caseIds, answerList, isAnswerListLoading]);
  const totalDuration = durationPerCaseInSeconds
    ? Math.ceil(Object.values(durationPerCaseInSeconds).reduce((acc, cur) => acc + cur, 0) / 60)
    : 0;
  const longestCaseDuration = durationPerCaseInSeconds
    ? Math.ceil(Math.max(...Object.values(durationPerCaseInSeconds)) / 60)
    : 0;

  const { engagementScore, caseIdToLLXRating, caseIdToLLXRatingDetails } =
    useMeetingEngagementScore(
      activated,
      caseIds,
      parsedSlideStats,
      parsedAssetViews,
      caseMap,
      isCaseLoading,
      consensusResultsPerCase,
      userMeetingJoins,
      uniqueCasesPresented
    );

  const meetingDate = useMemo(() => {
    if (!meetingDetails?.eventDate) return 0;
    return new Date(meetingDetails.eventDate).getTime();
  }, [meetingDetails?.eventDate]);

  const meetingHostName = meetingDetails?.hostFullName ?? '';

  if (!meetingID) {
    console.error('No meeting in url');
    return <EmptyPageWithTitle title="Sorry..." subtitle="The content was not found." />;
  }

  if (meetingDetailsQuery.error) {
    return <EmptyPageWithTitle title="Sorry..." subtitle="The content was not found." />;
  }

  if (meetingDetailsQuery.loading || isCaseLoading || isAnswerListLoading) {
    return <PageLoading />;
  }

  return (
    <MeetingStatsViewContext.Provider
      value={{
        currentPatientCaseId,
        setCurrentPatientCaseId,
        caseIds,
        caseTitles,
        parsedAssetViews: parsedAssetViews,
        parsedSlideStats: parsedSlideStats,
        consensusRate,
        consensusRatePerCase,
        consensusResultsPerCase,
        totalDuration,
        longestCaseDuration,
        durationPerCaseInSeconds,
        longestSlidePerCaseInSeconds,
        engagementScore,
        npsScore: npsAverage,
        amtNpsScores: npsScores.length,
        scoresPerUser: scoresPerUser,
        caseIdToLLXRating,
        caseIdToLLXRatingDetails,
        meetingDate,
        meetingLocation: meetingDetails?.eventLocation ?? MEETING_DEFAULT_LOCATION,
        meetingHostName,
        uniqueCasesPresented,
        answerList,
        maxAmtParticipants:
          meetingStatsData?.amountParticipants ||
          userMeetingJoinsQuery.data?.listUserMeetingJoins.edges.length ||
          0,
      }}
    >
      {children}
    </MeetingStatsViewContext.Provider>
  );
}
